import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ShieldAlert, Loader2, Upload, ExternalLink, Building2, Coins, Calendar, ShieldCheck, BadgeCheck } from "lucide-react";
import { Layout } from "@/components/Layout";
import { PlansModal } from "@/components/PlansModal";
import { OnboardingModal } from "@/components/OnboardingModal";
import { MediaManager } from "@/components/MediaManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, mediaUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { compressImage } from "@/lib/compressImage";

const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const isValidGst = (g) => GST_REGEX.test((g || "").trim().toUpperCase());
const UDYAM_REGEX = /^UDYAM-[A-Z]{2}-\d{2}-\d{7}$/;
const isValidUdyam = (u) => UDYAM_REGEX.test((u || "").trim().toUpperCase());

const empty = {
  business_name: "", category: "", category_other: "", gst_number: "", msme_number: "",
  city: "", state: "", phone: "", description: "", logo_url: "", cover_url: "",
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileRef = useRef(null);
  const coverRef = useRef(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [form, setForm] = useState(empty);
  const [categories, setCategories] = useState([]);
  const [existingId, setExistingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [plansOpen, setPlansOpen] = useState(false);
  const [onbOpen, setOnbOpen] = useState(false);
  const [bizData, setBizData] = useState(null);
  const [verifyPath, setVerifyPath] = useState("gst");

  useEffect(() => {
    Promise.all([
      api.get("/categories"),
      api.get("/businesses/me"),
    ]).then(([cats, mine]) => {
      setCategories(cats.data.categories);
      if (mine.data) {
        setExistingId(mine.data.id);
        setBizData(mine.data);
        setForm({ ...empty, ...mine.data, logo_url: mine.data.logo_url || "", cover_url: mine.data.cover_url || "" });
        const t = mine.data.verification_tier || (mine.data.gst_verified ? "gst" : mine.data.msme_number ? "msme" : "basic");
        setVerifyPath(t);
        if (!mine.data.onboarded) setOnbOpen(true);
      }
    }).catch((e) => console.error("Failed to load profile", e)).finally(() => setLoading(false));
  }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadImage = async (e, field, setBusy, label) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", await compressImage(file));
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [field]: data.logo_url }));
      toast.success(`${label} uploaded — click Save to apply`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  const handleUpload = (e) => uploadImage(e, "logo_url", setUploading, "Logo");
  const handleCoverUpload = (e) => uploadImage(e, "cover_url", setUploadingCover, "Banner");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category) return toast.error("Please choose a category");
    if (form.category === "Other" && !form.category_other?.trim()) return toast.error("Please specify your category");
    setSaving(true);
    try {
      const payload = { ...form };
      if (verifyPath === "gst") payload.msme_number = "";
      else if (verifyPath === "msme") payload.gst_number = "";
      else { payload.gst_number = ""; payload.msme_number = ""; }
      const { data } = await api.post("/businesses", payload);
      setExistingId(data.id);
      setBizData(data);
      setForm({ ...empty, ...data, logo_url: data.logo_url || "", cover_url: data.cover_url || "" });
      setVerifyPath(data.verification_tier || "basic");
      const msg = data.verification_tier === "gst" ? "Profile saved — GST Verified! (Blue Checkmark)"
        : data.verification_tier === "msme" ? "Profile saved — MSME Verified (Grey Checkmark)"
        : "Profile saved";
      toast.success(msg);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Layout><div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-brand" size={32} /></div></Layout>;
  }

  const gstValid = isValidGst(form.gst_number);
  const msmeValid = isValidUdyam(form.msme_number);

  return (
    <Layout>
      <div className="mx-auto max-w-2xl px-5 sm:px-8 py-10 sm:py-14">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Business Profile</span>
          {existingId && (
            <Button data-testid="view-public-profile-btn" onClick={() => navigate(`/business/${existingId}`)} variant="ghost" size="sm" className="gap-1.5 text-brand">
              View public profile <ExternalLink size={14} />
            </Button>
          )}
        </div>
        <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl text-slate-900">
          {existingId ? "Edit your profile" : "Create your profile"}
        </h1>
        <p className="mt-2 text-slate-500">Hi {user?.name || "there"} — fill in your details to appear in the directory.</p>

        {/* Subscription & credits panel */}
        <div data-testid="subscription-panel" className="mt-8 grid sm:grid-cols-3 gap-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-2 text-slate-400"><Coins size={16} /><span className="text-[11px] font-bold uppercase tracking-[0.12em]">Credits</span></div>
            <p data-testid="dashboard-credits" className="mt-2 font-display font-black text-3xl text-slate-900">{user?.credits ?? 0}</p>
            <p className="text-xs text-slate-400 mt-1">1 credit per feed post</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-2 text-slate-400"><ShieldCheck size={16} /><span className="text-[11px] font-bold uppercase tracking-[0.12em]">Active Plan</span></div>
            <p data-testid="dashboard-plan" className="mt-2 font-display font-black text-2xl text-slate-900 capitalize">
              {user?.plan_status === "active" ? (user.plan === "annual" ? "Annual" : "Basic") : "No plan"}
            </p>
            <p className="text-xs text-slate-400 mt-1">{user?.plan_status === "active" ? "Auto-renewing" : "Free trial credits"}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-2 text-slate-400"><Calendar size={16} /><span className="text-[11px] font-bold uppercase tracking-[0.12em]">Next Renewal</span></div>
            <p data-testid="dashboard-renewal" className="mt-2 font-display font-black text-lg text-slate-900">
              {user?.renewal_date ? new Date(user.renewal_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
            </p>
            <Button data-testid="buy-credits-btn" onClick={() => setPlansOpen(true)} size="sm" className="mt-3 w-full rounded-lg bg-brand hover:bg-brand-hover font-semibold">Buy Credits</Button>
          </div>
        </div>

        {existingId && (
          <div className="mt-4 flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-5">
            <div>
              <p className="font-display font-bold text-slate-900">Business preferences</p>
              <p className="text-sm text-slate-500">{bizData?.intents?.length ? bizData.intents.join(", ") : "Set what you're looking for to personalise Discover"}</p>
            </div>
            <Button data-testid="edit-preferences-btn" onClick={() => setOnbOpen(true)} variant="outline" className="border-slate-300 font-semibold shrink-0">Edit</Button>
          </div>
        )}

        {existingId && form.verification_tier !== "gst" && (
          <div data-testid="gst-nudge" className="mt-4 flex items-center gap-3 rounded-2xl border border-blue-200 bg-blue-50 p-4">
            <BadgeCheck className="text-brand shrink-0" size={24} />
            <div className="flex-1 min-w-0">
              <p className="font-bold text-slate-900 text-sm">Get the Blue Checkmark</p>
              <p className="text-sm text-slate-600">Add your GST for <strong>2x more visibility</strong> and top ranking in Discover.</p>
            </div>
            <Button type="button" data-testid="gst-nudge-btn" size="sm" onClick={() => { setVerifyPath("gst"); setTimeout(() => document.getElementById("gst")?.scrollIntoView({ behavior: "smooth", block: "center" }), 50); }} className="bg-brand hover:bg-brand-hover font-semibold shrink-0">Add GST</Button>
          </div>
        )}

        {existingId && <MediaManager businessId={existingId} />}

        <motion.form
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          onSubmit={submit}
          className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm space-y-8"
        >
          {/* Logo */}
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 mb-3">Logo</p>
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 rounded-xl border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center shrink-0">
                {form.logo_url ? (
                  <img src={mediaUrl(form.logo_url)} alt="logo" className="h-full w-full object-cover" />
                ) : (
                  <Building2 className="text-slate-300" size={28} />
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="logo-file-input" />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="logo-upload-btn" className="gap-2 border-slate-300">
                {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
                {form.logo_url ? "Change logo" : "Upload logo"}
              </Button>
            </div>
            <p data-testid="logo-tip" className="mt-2 text-xs text-slate-400">Best results with square images. Large photos are auto-optimised.</p>
          </div>

          {/* Banner */}
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 mb-3">Banner image</p>
            <div data-testid="cover-preview" className="relative h-32 sm:h-40 w-full rounded-xl border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center">
              {form.cover_url ? (
                <img src={mediaUrl(form.cover_url)} alt="banner" className="h-full w-full object-cover" />
              ) : (
                <p className="text-sm text-slate-400">No banner yet</p>
              )}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <input ref={coverRef} type="file" accept="image/*" onChange={handleCoverUpload} className="hidden" data-testid="cover-file-input" />
              <Button type="button" variant="outline" onClick={() => coverRef.current?.click()} disabled={uploadingCover} data-testid="cover-upload-btn" className="gap-2 border-slate-300">
                {uploadingCover ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
                {form.cover_url ? "Change banner" : "Upload banner"}
              </Button>
              {form.cover_url && (
                <Button type="button" variant="ghost" onClick={() => set("cover_url")("")} data-testid="cover-remove-btn" className="text-slate-500">Remove</Button>
              )}
            </div>
            <p data-testid="cover-tip" className="mt-2 text-xs text-slate-400">Wide landscape image recommended (e.g. 1600×500).</p>
          </div>

          {/* Identity */}
          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="bn">Business name *</Label>
              <Input data-testid="business-name-input" id="bn" required value={form.business_name} onChange={(e) => set("business_name")(e.target.value)} placeholder="e.g. Sunrise Traders Pvt Ltd" className="bg-slate-50 h-11" />
            </div>

            <div className="space-y-2">
              <Label>Category *</Label>
              <Select value={form.category} onValueChange={set("category")}>
                <SelectTrigger data-testid="category-select" className="bg-slate-50 h-11">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`category-option-${c}`}>{c}</SelectItem>
                  ))}
                  <SelectItem value="Other" data-testid="category-option-Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.category === "Other" && (
              <div className="space-y-2">
                <Label htmlFor="co">Specify category *</Label>
                <Input data-testid="category-other-input" id="co" value={form.category_other} onChange={(e) => set("category_other")(e.target.value)} placeholder="Your industry" className="bg-slate-50 h-11" />
              </div>
            )}
          </div>

          {/* Verification */}
          <div className="space-y-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 mb-1">Verification</p>
              <p className="text-sm text-slate-500">Choose how to verify. Verified businesses rank higher in Discover and earn more trust.</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              {[
                { id: "gst", title: "GST", sub: "Blue Checkmark", icon: BadgeCheck, color: "text-brand" },
                { id: "msme", title: "MSME / Udyam", sub: "Grey Checkmark", icon: BadgeCheck, color: "text-slate-400" },
                { id: "basic", title: "Basic", sub: "No badge", icon: Building2, color: "text-slate-300" },
              ].map((p) => (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`verify-path-${p.id}`}
                  onClick={() => setVerifyPath(p.id)}
                  className={`text-left rounded-xl border p-3 transition-colors ${verifyPath === p.id ? "border-brand bg-brand-light" : "border-slate-200 bg-white hover:border-slate-300"}`}
                >
                  <p.icon size={18} className={p.color} />
                  <p className="mt-1.5 font-bold text-sm text-slate-900">{p.title}</p>
                  <p className="text-[11px] text-slate-500">{p.sub}</p>
                </button>
              ))}
            </div>

            {verifyPath === "gst" && (
              <div className="space-y-2">
                <Label htmlFor="gst">GST Number</Label>
                <Input data-testid="gst-input" id="gst" value={form.gst_number} onChange={(e) => set("gst_number")(e.target.value.toUpperCase())} placeholder="27AAPFU0939F1ZV" className="bg-slate-50 h-11 font-mono tracking-wide" />
                {form.gst_number && (gstValid ? (
                  <span data-testid="gst-verified-badge" className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 border border-blue-200 px-3 py-1 text-xs font-bold text-brand mt-1">
                    <BadgeCheck size={14} className="text-brand" /> Valid GSTIN — Blue Checkmark
                  </span>
                ) : (
                  <span data-testid="gst-invalid-badge" className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-xs font-bold text-amber-700 mt-1">
                    <ShieldAlert size={14} /> Invalid format — 15-char GSTIN required
                  </span>
                ))}
              </div>
            )}

            {verifyPath === "msme" && (
              <div className="space-y-2">
                <Label htmlFor="msme">MSME / Udyam Number</Label>
                <Input data-testid="msme-input" id="msme" value={form.msme_number} onChange={(e) => set("msme_number")(e.target.value.toUpperCase())} placeholder="UDYAM-MH-13-1234567" className="bg-slate-50 h-11 font-mono tracking-wide" />
                {form.msme_number && (msmeValid ? (
                  <span data-testid="msme-verified-badge" className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 border border-slate-200 px-3 py-1 text-xs font-bold text-slate-600 mt-1">
                    <BadgeCheck size={14} className="text-slate-400" /> Valid Udyam — Grey Checkmark
                  </span>
                ) : (
                  <span data-testid="msme-invalid-badge" className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-xs font-bold text-amber-700 mt-1">
                    <ShieldAlert size={14} /> Format: UDYAM-XX-00-0000000
                  </span>
                ))}
              </div>
            )}

            {verifyPath === "basic" && (
              <p data-testid="basic-path-note" className="text-sm text-slate-500 rounded-xl border border-dashed border-slate-300 p-4">
                Your business will appear as <strong>Unverified</strong> (no checkmark). Add GST or MSME anytime to rank higher.
              </p>
            )}
          </div>

          {/* Contact */}
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input data-testid="city-input" id="city" value={form.city} onChange={(e) => set("city")(e.target.value)} placeholder="Mumbai" className="bg-slate-50 h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="state">State</Label>
              <Input data-testid="state-input" id="state" value={form.state} onChange={(e) => set("state")(e.target.value)} placeholder="Maharashtra" className="bg-slate-50 h-11" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input data-testid="phone-input" id="phone" value={form.phone} onChange={(e) => set("phone")(e.target.value)} placeholder="+91 98765 43210" className="bg-slate-50 h-11" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="desc">Description</Label>
            <Textarea data-testid="description-input" id="desc" value={form.description} onChange={(e) => set("description")(e.target.value)} placeholder="Tell others what your business does…" className="bg-slate-50 min-h-[110px]" />
          </div>

          <Button data-testid="save-profile-btn" type="submit" disabled={saving} className="w-full h-12 rounded-lg bg-brand hover:bg-brand-hover font-semibold text-base gap-2">
            {saving && <Loader2 className="animate-spin" size={18} />}
            {existingId ? "Save Changes" : "Publish Profile"}
          </Button>
        </motion.form>
      </div>
      <PlansModal open={plansOpen} onOpenChange={setPlansOpen} />
      {bizData && <OnboardingModal open={onbOpen} onOpenChange={setOnbOpen} business={bizData} onDone={() => { setBizData({ ...bizData, onboarded: true }); }} />}
    </Layout>
  );
}
